/*
 * Lễ tân tại cơ sở:
 *  Hàng chờ, phòng, nhân sự lấy từ /api/v2/reception; mọi thao tác ghi lên server rồi mới cập nhật màn hình.
 *  1. Hai hàng chờ (họp 30/10): Hàng chờ tư vấn (khách mới) và Hàng chờ thực hiện (khách đã có lịch làm DV / liệu trình / đã mua).
 *     Lịch đặt trước gộp vào hàng chờ, chỉ khác trạng thái "Đặt trước"; xem theo danh sách, theo phòng, theo bác sĩ, theo KTV.
 *     /reception-queue, /reception-booking chuyển về đây.
 *  2. Khách đã check-in (kiosk / Lễ tân) → phân tư vấn viên cơ sở + giữ phòng tư vấn trống; khách vào "Khách đang chờ" của tư vấn viên đó.
 *  3. Khách làm dịch vụ trong ngày → xếp phòng thủ thuật + kỹ thuật viên đang trống, xong thì trả phòng.
 * Lượt tư vấn dùng chung store App Tư vấn (useTuVan) nên hai màn thấy cùng danh sách khách.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import dayjs, { type Dayjs } from "dayjs";
import { Alert, Button, DatePicker, Empty, Input, Popconfirm, Segmented, Select, Switch, Table, Tag, Tooltip, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { AlertTriangle, CalendarClock, CalendarRange, DoorOpen, ExternalLink, QrCode, Search, Shuffle, Stethoscope, UserCheck, Users } from "lucide-react";
import Layout from "../../Layout";
import SoftBar from "./Softbar";
import Timeline, { type Marker } from "./Timeline";
import { currentStaffName, useLeadStore } from "../Telesales/lead/store";
import { useTuVan } from "../AppTuVan/store";
import { DeclBadge, useNow, useVisitSync, who } from "../AppTuVan/shared";
import { assignVisit, curBranch, ensureServerVisit, execDoneVisit, execVisit, finishVisit, patchVisit, reloadVisits, useBoardRefs, useReception, type ActResult } from "../AppTuVan/receptionApi";
import { queueOfApptType } from "../AppTuVan/receptionServer";
import { API_CHECKED_IN, apptsOfDay, phoneKey } from "../AppTuVan/tele";
import { STAFF_ST, VISIT_ST, activePlans, allCodes, branchIdByName, doctorName, inExecRoom, isToday, minutes, nextCode, techName, type ExecPlan, type Session, type Visit } from "../AppTuVan/logic";
import {
  CONS_ST, WARN_TEXT, consultantBoard, doctorBoard, freeRooms, planAuto, roomBoard, suggestConsultant, suggestTech, techBoard, timeline, todaySessions, warnsOf,
  type TimelineKind,
} from "../AppTuVan/reception";
import { kioskCheckin, kioskDayAppointments } from "../CheckIn/kioskApi";

/** Quá N phút sau giờ hẹn chưa check-in thì hiện "Trễ" */
const LATE_MIN = 15;
const hm = (t?: number) => (t ? dayjs(t).format("HH:mm") : "—");
const WD = ["Chủ nhật", "Thứ 2", "Thứ 3", "Thứ 4", "Thứ 5", "Thứ 6", "Thứ 7"];
/** Thứ 6, 30/10/2026 */
const viDay = (t: number) => `${WD[dayjs(t).day()]}, ${dayjs(t).format("DD/MM/YYYY")}`;
/** 25′ · 2g05′ */
const dur = (m: number) => (m < 60 ? `${m}′` : `${Math.floor(m / 60)}g${String(m % 60).padStart(2, "0")}′`);
const vnd = (n: number) => `${n.toLocaleString("vi-VN")} đ`;

/* ---------- Lịch hẹn đăng ký trước ---------- */

type BookSt = "Chưa đến" | "Trễ" | "Đã check-in" | "Không đến";
const BOOK_ST: Record<BookSt, string> = { "Chưa đến": "default", "Trễ": "red", "Đã check-in": "green", "Không đến": "volcano" };
interface Booking {
  key: string;
  at: number;
  code: string;
  name: string;
  phone: string;
  services: string;
  source: string;
  owner?: string;
  status: BookSt;
  /** Lịch trên hệ thống — check-in qua API như kiosk */
  apiId?: number;
  /** Lịch làm dịch vụ / tái khám → Hàng chờ thực hiện */
  exec: boolean;
  /** Lịch Telesales chỉ có trên máy */
  leadId?: number;
  apptId?: string;
  visit?: Visit;
  /** Buổi thực hiện App Tư vấn đã xếp cho hôm nay (khách tư vấn từ ngày trước) */
  plan?: { from: Visit; p: ExecPlan; s: Session };
}

/* eslint-disable @typescript-eslint/no-explicit-any */
function bookingsOf(api: any[], byId: ReturnType<typeof useLeadStore.getState>["byId"], visits: Visit[], now: number, day: number, branchId: string): Booking[] {
  // "Trễ" chỉ tính cho lịch hôm nay
  const late = (at: number) => dayjs(day).isSame(now, "day") && now - at > LATE_MIN * 60_000;
  const fromApi = api.filter((a) => Number(a?.status) !== 0).map((a): Booking => {
    const at = dayjs(String(a.scheduled_at).replace(" ", "T")).valueOf();
    // Lượt đã huỷ: lịch hiện lại trong Đặt trước để check-in lại, server sẽ mở lại lượt
    const visit = visits.find((v) => v.apiApptId === a.id && v.status !== "cancelled");
    const cancelled = visits.some((v) => v.apiApptId === a.id && v.status === "cancelled");
    const came = !!visit || (Number(a?.customer_tag_status) === API_CHECKED_IN && !cancelled);
    return {
      key: `api-${a.id}`, at, apiId: a.id, visit,
      code: a.customer?.customer_code || a.code || `#${a.id}`,
      name: a.customer_name || a.customer?.fullname || "Khách",
      phone: String(a.customer_phone || a.customer?.phone || ""),
      services: (a.services ?? []).map((s: any) => s?.name).filter(Boolean).join(", ") || a.note || "—",
      source: a.telesale ? "Telesales" : "Hệ thống",
      owner: a.telesale?.fullname || a.telesale?.name,
      status: came ? "Đã check-in" : late(at) ? "Trễ" : "Chưa đến",
      exec: queueOfApptType(a.appointment_type) === "exec",
    };
  });
  const onServer = new Set(api.map((a) => a.id));
  // Lịch hẹn API đã lọc theo cơ sở, lịch Telesales trên máy thì lọc theo tên cơ sở; không vậy lịch cơ sở khác lọt vào đây vì không nằm trong danh sách API
  const inBranch = (name?: string) => { const id = branchIdByName(name); return !id || id === branchId; };
  const local = apptsOfDay(byId, day).filter(({ a }) => inBranch(a.branch) && (!a.serverId || !onServer.has(a.serverId))).map(({ leadId, a, name, code }): Booking => {
    const visit = visits.find((v) => v.apptId === a.id);
    const came = !!visit || a.status === "arrived" || a.status === "done";
    return {
      key: `tele-${a.id}`, at: a.at, leadId, apptId: a.id, visit, code, name,
      phone: String(a.cust?.phone ?? ""),
      services: a.services?.map((s) => s.name).join(", ") || a.service || a.note || "—",
      source: "Telesales", owner: a.telesale?.name ?? a.by,
      status: came ? "Đã check-in" : a.status === "noshow" ? "Không đến" : late(a.at) ? "Trễ" : "Chưa đến",
      exec: queueOfApptType(a.type) === "exec",
    };
  });
  return [...fromApi, ...local].sort((x, y) => x.at - y.at);
}
/* eslint-enable @typescript-eslint/no-explicit-any */

/** Mã lượt làm dịch vụ theo một buổi của lịch thực hiện — check-in lại không tạo trùng */
const execVisitId = (v: Visit, p: ExecPlan, s: Session) => `exec-${v.id}-${p.id}-${s.no}`;

/* ---------- Khung nhỏ ---------- */

const Box = ({ title, icon, extra, children }: { title: string; icon?: React.ReactNode; extra?: React.ReactNode; children: React.ReactNode }) => (
  <section className="min-w-0 rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900 sm:p-4">
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <h2 className="flex items-center gap-1.5 text-[15px] font-bold text-slate-800 dark:text-slate-100">{icon}{title}</h2>
      {extra}
    </div>
    {children}
  </section>
);

/** Dải số liệu gọn: mỗi ô bấm được để mở đúng danh sách */
interface SumItem { label: string; value: React.ReactNode; warn?: boolean; good?: boolean; onClick?: () => void; hint?: string }
const Summary = ({ title, items }: { title: string; items: SumItem[] }) => (
  <section className="min-w-0 rounded-lg border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
    <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{title}</div>
    <div className="flex flex-wrap gap-1.5">
      {items.map((it) => (
        <Tooltip key={it.label} title={it.hint}>
          <button
            type="button"
            onClick={it.onClick}
            className={`flex min-w-[96px] flex-1 flex-col rounded-md border px-2.5 py-1.5 text-left transition-colors hover:border-[#3e91ab] ${it.warn
              ? "border-orange-300 bg-orange-50 dark:border-orange-500/40 dark:bg-orange-500/10"
              : "border-slate-100 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/60"}`}
          >
            <span className="text-[11.5px] text-slate-500 dark:text-slate-400">{it.label}</span>
            <span className={`text-lg font-bold leading-tight ${it.warn ? "text-orange-600" : it.good ? "text-emerald-600 dark:text-emerald-400" : "text-slate-900 dark:text-slate-50"}`}>{it.value}</span>
          </button>
        </Tooltip>
      ))}
    </div>
  </section>
);

/** Lọc hàng chờ theo việc Lễ tân cần làm */
type QFilter = "open" | "warn" | "unassigned" | "held" | "consulting" | "exec" | "done";

/** Bộ lọc hàng chờ: chung "Tất cả" / "Đặt trước" + riêng từng hàng (tư vấn / thực hiện) */
type Filter = QFilter | "all" | "booked" | "wait" | "busy" | "doing";
/** Một dòng hàng chờ: khách đã check-in (v) hoặc lịch đặt trước chưa đến (b) */
interface QRow { key: string; v?: Visit; b?: Booking }

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <>
    <dt className="text-slate-500 dark:text-slate-400">{label}</dt>
    <dd className="min-w-0 break-words">{children}</dd>
  </>
);

/** Tình trạng lượt theo góc nhìn Lễ tân */
const flowOf = (v: Visit, now: number) => {
  if (v.exec && !v.exec.doneAt) return { label: `Đang làm DV · ${v.exec.room}`, color: "purple" };
  if (v.exec?.doneAt && v.queue === "exec") return { label: "Đã xong dịch vụ", color: "green" };
  const sess = !v.exec ? todaySessions(v, now)[0] : undefined;
  if (sess && v.status !== "waiting") return { label: `Chờ làm DV · hẹn ${hm(sess.s.at)}`, color: "magenta" };
  if (v.execOwner && !v.exec) return { label: v.execOwner.waitAt ? "Chờ người phụ trách" : "Chờ làm DV", color: "magenta" };
  if (v.status === "waiting") return v.consultant ? { label: "Chờ tư vấn viên nhận", color: "gold" } : { label: "Chờ phân tư vấn", color: "red" };
  return { label: VISIT_ST[v.status].label, color: VISIT_ST[v.status].color };
};

/* ---------- Trang ---------- */

export default function ReceptionCheckIn() {
  useVisitSync();
  const now = useNow(30_000);
  const by = useMemo(currentStaffName, []);
  const { visits, branchId, staffSt, autoAssign, update, set, add } = useTuVan();
  const refs = useBoardRefs();
  const branches = useReception((s) => s.branches);
  const syncError = useReception((s) => s.error);
  const byId = useLeadStore((s) => s.byId);

  // Lịch hẹn trong ngày trên hệ thống (chỉ đọc), tải lại mỗi phút
  const [api, setApi] = useState<any[]>([]); // eslint-disable-line @typescript-eslint/no-explicit-any
  const [apiErr, setApiErr] = useState(false);
  const [tick, setTick] = useState(0);
  const [params, setParams] = useSearchParams();
  // Lịch đặt trước xem theo ngày: hôm nay (mặc định), ngày mai để gọi xác nhận, hoặc ngày bất kỳ
  const [day, setDay] = useState<Dayjs>(() => dayjs().startOf("day"));
  const isTodayView = day.isSame(now, "day");
  const dateKey = `${day.format("YYYY-MM-DD")}|${dayjs(now).format("YYYY-MM-DD")}`;
  useEffect(() => {
    let alive = true;
    const pull = () => kioskDayAppointments(day.format("YYYY-MM-DD"), curBranch() || undefined).then((res) => {
      if (!alive) return;
      setApiErr(!res.status);
      if (res.status) setApi(res.data);
    });
    pull();
    const t = setInterval(pull, 60_000);
    return () => { alive = false; clearInterval(t); };
  }, [dateKey, tick, branchId]); // eslint-disable-line react-hooks/exhaustive-deps

  const today = useMemo(() => visits.filter((v) => v.branchId === branchId && v.status !== "referral" && v.status !== "cancelled" && isToday(v.checkInAt, now)), [visits, branchId, now]);
  // Buổi thực hiện App Tư vấn xếp cho ngày đang xem (khách tư vấn từ hôm trước) → dòng "Đặt trước" của Hàng chờ thực hiện
  const planBooks = useMemo((): Booking[] => visits
    .filter((v) => v.branchId === branchId && !dayjs(v.checkInAt ?? v.createdAt).isSame(day, "day"))
    .flatMap((v) => activePlans(v).flatMap((p) => p.sessions
      .filter((s) => s.at && s.status !== "unscheduled" && dayjs(s.at).isSame(day, "day"))
      .map((s): Booking => {
        const visit = visits.find((x) => x.id === execVisitId(v, p, s));
        const late = isTodayView && now - s.at! > LATE_MIN * 60_000;
        return {
          key: `plan-${v.id}-${p.id}-${s.no}`, at: s.at!, code: v.cust.code, name: v.cust.name, phone: v.phoneDigits ?? "",
          services: `${p.line} · buổi ${s.no}/${p.sessions.length}`, source: "App Tư vấn", owner: doctorName(s.doctor ?? p.doctor),
          status: visit ? "Đã check-in" : late ? "Trễ" : "Chưa đến", exec: true, visit, plan: { from: v, p, s },
        };
      }))), [visits, branchId, day, now, isTodayView]);
  const bookings = useMemo(() => [...bookingsOf(api, byId, visits, now, day.valueOf(), branchId), ...planBooks].sort((x, y) => x.at - y.at), [api, byId, visits, now, day, planBooks, branchId]);
  const rooms = useMemo(() => roomBoard(visits, branchId, now, refs), [visits, branchId, now, refs]);
  const cons = useMemo(() => consultantBoard(visits, branchId, staffSt, now, refs), [visits, branchId, staffSt, now, refs]);
  const techs = useMemo(() => techBoard(visits, branchId, now, refs), [visits, branchId, now, refs]);
  const docs = useMemo(() => doctorBoard(visits, branchId, now, refs), [visits, branchId, now, refs]);
  const warns = useMemo(() => new Map(today.map((v) => [v.id, warnsOf(v, now)])), [today, now]);

  // Khách cần Lễ tân xử lý lên trước: có cảnh báo → chưa phân → chờ nhận → chờ làm DV → đang tư vấn → xong
  const order = (v: Visit) => (warns.get(v.id)?.length ? 0 : v.status === "waiting" && !v.consultant ? 1 : v.status === "waiting" ? 2
    : !v.exec && todaySessions(v, now).length ? 3 : v.status === "consulting" ? 4 : 5);
  const queue = [...today].sort((a, b) => order(a) - order(b) || (a.checkInAt ?? 0) - (b.checkInAt ?? 0));

  /** Khách thuộc Hàng chờ thực hiện: đang / sẽ làm DV hôm nay, khách thẻ liệu trình, đã có người phụ trách làm DV */
  // Khách thẻ liệu trình chuyển sang (cardPhone) là cần tư vấn thêm → Hàng chờ tư vấn
  // Hàng do server xếp (Tái khám ở hàng tư vấn); khách hàng tư vấn được xếp làm dịch vụ thì sang hàng thực hiện
  const isExec = (v: Visit) => v.queue === "exec" || !!v.exec || todaySessions(v, now).length > 0 || (!v.serverId && !!v.execOwner);
  /** KTV / bác sĩ phụ trách khách làm DV: Lễ tân đã chọn → buổi hẹn → bác sĩ khám */
  const ownerOf = (v: Visit) => ({
    tech: v.execOwner?.tech ?? v.exec?.tech,
    doctor: v.execOwner?.doctor ?? v.exec?.doctor ?? todaySessions(v, now)[0]?.s.doctor ?? v.quote?.exam.doctor,
  });
  /** Người phụ trách đang bận với khách khác (báo đỏ) */
  const ownerBusy = (v: Visit) => {
    if (v.exec) return undefined;
    const o = ownerOf(v);
    const t = o.tech ? techs.find((x) => x.tech.id === o.tech && x.visit && x.visit.id !== v.id) : undefined;
    const d = o.doctor ? docs.find((x) => x.id === o.doctor && x.visit && x.visit.id !== v.id) : undefined;
    if (!t && !d) return undefined;
    return [t && `${t.tech.name} đang làm cho ${t.visit!.cust.name}`, d && `${d.name} đang ${d.why === "exec" ? "làm DV" : "khám"} ${d.visit!.cust.name}`].filter(Boolean).join(" · ");
  };

  type Tab = "consult" | "exec" | "calendar";
  const rawTab = params.get("tab");
  const tab: Tab = rawTab === "exec" ? "exec" : rawTab === "calendar" ? "calendar" : "consult";
  const setTab = (t: Tab, f: Filter = "all") => {
    // Lịch theo giờ là lịch hôm nay
    if (t === "calendar") setDay(dayjs().startOf("day"));
    // Đổi hàng chờ thì khung bên phải mở khách của hàng đó
    if (t !== tab) setSel(undefined);
    setQf(f);
    setParams((p) => { p.set("tab", t); return p; }, { replace: true });
  };
  /** Xem danh sách, hoặc dạng cột theo phòng / bác sĩ / người phụ trách (tư vấn viên hoặc KTV) */
  const [view, setView] = useState<"list" | "room" | "doctor" | "staff">("list");
  const [tlKind, setTlKind] = useState<TimelineKind>("cons");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState<string>();
  const match = (name: string, code: string, phone: string) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    const d = phoneKey(s);
    return name.toLowerCase().includes(s) || code.toLowerCase().includes(s) || (!!d && d.length >= 3 && phoneKey(phone).includes(d));
  };
  const execPending = (v: Visit) => (v.exec ? !v.exec.doneAt : todaySessions(v, now).length > 0);
  const QF: Record<QFilter, (v: Visit) => boolean> = {
    open: (v) => v.status !== "done" || execPending(v),
    warn: (v) => !!warns.get(v.id)?.length,
    unassigned: (v) => v.status === "waiting" && !v.consultant,
    held: (v) => v.status === "waiting" && !!v.consultant,
    consulting: (v) => v.status === "consulting",
    exec: execPending,
    done: (v) => v.status === "done" && !execPending(v),
  };
  // Bộ lọc của từng hàng chờ; "Tất cả" = khách đã check-in + lịch đặt trước chưa đến (gộp Lịch đặt trước, chỉ khác trạng thái)
  const FILTERS: Record<"consult" | "exec", [Filter, string, ((v: Visit) => boolean) | null][]> = {
    consult: [
      ["all", "Tất cả", null], ["booked", "Đặt trước", null], ["warn", "Cần xử lý", QF.warn], ["unassigned", "Chờ phân", QF.unassigned],
      ["held", "Chờ nhận", QF.held], ["consulting", "Đang tư vấn", QF.consulting], ["done", "Đã xong", (v) => v.status === "done"],
    ],
    exec: [
      ["all", "Tất cả", null], ["booked", "Đặt trước", null], ["wait", "Chờ làm", (v) => !v.exec && v.status !== "done"], ["busy", "Người phụ trách bận", (v) => !!ownerBusy(v)],
      ["doing", "Đang làm", (v) => !!v.exec && !v.exec.doneAt], ["done", "Đã xong", (v) => !!v.exec?.doneAt],
    ],
  };
  const [qf, setQf] = useState<Filter>(rawTab === "booked" ? "booked" : "all");
  const lane: "consult" | "exec" = tab === "exec" ? "exec" : "consult";
  const laneVisits = queue.filter((v) => isExec(v) === (lane === "exec"));
  const laneBooks = bookings.filter((b) => b.exec === (lane === "exec") && b.status !== "Đã check-in");
  const ftest = FILTERS[lane].find((f) => f[0] === qf)?.[2];
  const rows: QRow[] = [
    ...(qf === "booked" ? [] : laneVisits.filter((v) => !ftest || ftest(v)).map((v) => ({ key: v.id, v }))),
    ...(qf === "all" || qf === "booked" ? laneBooks.map((b) => ({ key: b.key, b })) : []),
  ].filter((r) => (r.v ? match(r.v.cust.name, r.v.cust.code, r.v.phoneDigits ?? "") : match(r.b!.name, r.b!.code, r.b!.phone)));
  const countOf = (f: Filter, l: "consult" | "exec") => {
    const vs = queue.filter((v) => isExec(v) === (l === "exec"));
    const bs = bookings.filter((b) => b.exec === (l === "exec") && b.status !== "Đã check-in").length;
    const t = FILTERS[l].find((x) => x[0] === f)?.[2];
    return f === "all" ? vs.length + bs : f === "booked" ? bs : vs.filter(t!).length;
  };
  const showQueue = (f: QFilter) => setTab("consult", f);
  const showCal = (k: TimelineKind) => { setTlKind(k); setTab("calendar"); };

  const visit = today.find((v) => v.id === sel);
  const booking = !visit ? bookings.find((b) => b.key === sel) : undefined;
  // Mặc định mở khách đầu tiên cần xử lý
  useEffect(() => { if (!sel && laneVisits[0]) setSel(laneVisits[0].id); }, [sel, laneVisits]);

  const waitingNew = today.filter((v) => v.status === "waiting" && !v.consultant && !v.execOwner && !inExecRoom(v)).length;
  const freeConsultRooms = rooms.filter((r) => r.kind === "consult" && !r.visit).length;
  const freeExecRooms = rooms.filter((r) => r.kind === "exec" && !r.visit).length;
  const freeCons = cons.filter((c) => c.st === "free").length;
  const freeTechs = techs.filter((t) => !t.visit).length;
  const freeDocs = docs.filter((d) => !d.visit).length;
  const warnCount = [...warns.values()].filter((w) => w.length).length;
  const notCome = bookings.filter((b) => b.status === "Chưa đến" || b.status === "Trễ").length;

  /* ---------- Thao tác ---------- */

  const [busy, setBusy] = useState(false);
  /** Gọi server; lỗi thì báo, danh sách đã được tải lại trong lớp API */
  const run = async (p: Promise<ActResult>, ok?: string) => {
    setBusy(true);
    const r = await p;
    setBusy(false);
    if (!r.ok) message.error(r.msg);
    else if (ok) message.success(ok);
    return r.ok;
  };
  const checkIn = async (b: Booking) => {
    // Buổi thực hiện đã xếp ở App Tư vấn: tạo lượt mới hôm nay, mang sẵn bác sĩ / phòng / buổi → Hàng chờ thực hiện
    if (b.plan) {
      const { from, p, s } = b.plan;
      const at = Date.now();
      const nv: Visit = {
        id: execVisitId(from, p, s), code: nextCode("LV", allCodes(visits), at), branchId: from.branchId, createdAt: at,
        cust: from.cust, phoneDigits: from.phoneDigits, mang: from.mang, need: b.services, source: "Khách cũ", owner: from.owner,
        visitNo: from.visitNo + 1, checkInAt: at, status: "waiting", note: "", deposits: [],
        plans: [{ ...p, sessions: [s] }], execOwner: { doctor: s.doctor ?? p.doctor }, screening: from.screening,
        log: [{ at, text: `Check-in làm ${b.services} · hẹn ${hm(s.at)}`, by }],
      };
      add([nv]);
      setBusy(true);
      const created = await ensureServerVisit(nv);
      // Bác sĩ theo lịch thực hiện; mã cũ (BS-01) không có trên server thì Lễ tân chọn lại
      const docId = Number(nv.execOwner?.doctor);
      if (created.ok && created.visit && docId) await patchVisit(created.visit, { owner_doctor_id: docId });
      setBusy(false);
      if (!created.ok) { message.warning(`Đã ghi check-in trên máy, chưa tạo được lượt trên hệ thống: ${created.msg}`); }
      else message.success(`Đã check-in ${b.name} — khách vào Hàng chờ thực hiện`);
      setTab("exec");
      setSel(nv.id);
      return;
    }
    if (b.leadId && b.apptId) {
      // Lịch Telesales trên máy → "Đã đến cơ sở"; đồng bộ tạo lượt tư vấn
      useLeadStore.getState().setApptStatus(b.leadId, b.apptId, "arrived", by);
      message.success(`Đã check-in ${b.name}`);
      setTab(b.exec ? "exec" : "consult");
      return;
    }
    if (!b.apiId) return;
    setBusy(true);
    const res = await kioskCheckin({ id: b.apiId, confirm: true });
    setBusy(false);
    const action = res.data?.action;
    if (!res.status || (action && action !== "checkin_success" && action !== "already_in_progress")) {
      message.error(res.msg || "Không check-in được lịch này — kiểm tra lại ở màn Lịch hẹn.");
      return;
    }
    // Server tạo lượt lúc check-in: tải ngay để khách vào hàng chờ, không chờ đến lần làm mới sau
    await reloadVisits();
    setTick((t) => t + 1);
    const sv = (res.data as { visit?: { id: number; queue: "consult" | "exec"; reopened?: boolean } } | null)?.visit;
    const nv = sv ? useTuVan.getState().visits.find((x) => x.serverId === sv.id) : undefined;
    // Lượt đã xong thì server trả lại lượt cũ, khách không vào lại hàng chờ
    if (nv?.status === "done") {
      message.info(`Lượt ${nv.code} của lịch này đã xong, khách không vào lại hàng chờ. Cần tiếp đón thì tạo lượt mới ở App Tư vấn, nút Thêm khách.`);
      return;
    }
    const toExec = (sv?.queue ?? (b.exec ? "exec" : "consult")) === "exec";
    const lane = toExec ? "Hàng chờ thực hiện" : "Hàng chờ tư vấn";
    message.success(sv?.reopened ? `Đã mở lại lượt đã huỷ của ${b.name} — khách vào lại ${lane}` : `Đã check-in ${b.name} — khách vào ${lane}`);
    setTab(toExec ? "exec" : "consult");
    if (nv) setSel(nv.id);
  };

  const logTo = (r: boolean, id: string, text: string) => { if (r) update(id, () => ({}), { text, by }); return r; };
  const assign = async (v: Visit, consultant: string, room: string) => logTo(
    await run(assignVisit(v, consultant, room || undefined), `Đã phân ${v.cust.name} cho ${who(consultant)}${room ? ` · ${room}` : ""}`),
    v.id,
    `Lễ tân phân cho ${who(consultant)}${room ? ` · giữ ${room}` : ""}`,
  );
  /** Chia hết khách đang chờ chưa có người: tư vấn viên rảnh, ít lượt + phòng tư vấn trống */
  const autoAll = async () => {
    const picks = planAuto(visits, branchId, staffSt, Date.now(), refs);
    if (!picks.length) { message.info("Không còn tư vấn viên trống để chia"); return; }
    let n = 0;
    setBusy(true);
    for (let i = 0; i < picks.length; i += 1) {
      const p = picks[i];
      const cur = useTuVan.getState().visits.find((x) => x.id === p.id);
      // eslint-disable-next-line no-await-in-loop
      const r = cur ? await assignVisit(cur, p.consultant, p.room) : undefined;
      if (r?.ok) { n += 1; update(p.id, () => ({}), { text: `Lễ tân chia tự động cho ${who(p.consultant)}${p.room ? ` · giữ ${p.room}` : ""}`, by }); }
      else if (r) message.error(`${cur?.cust.name}: ${r.msg}`);
    }
    setBusy(false);
    if (n) message.success(`Đã chia ${n} khách`);
  };
  const startExec = async (v: Visit, room: string, tech: string, doctor?: string) => logTo(
    await run(execVisit(v, room, tech, doctor), `Đã xếp ${v.cust.name} vào ${room} · ${techName(tech)}${doctor ? ` · ${doctorName(doctor)}` : ""}`),
    v.id,
    `Lễ tân xếp làm dịch vụ · ${room} · ${techName(tech)}${doctor ? ` · ${doctorName(doctor)}` : ""}`,
  );
  /** Hàng chờ thực hiện: chọn / đổi người phụ trách, báo người phụ trách, khách chấp nhận chờ */
  const setOwner = async (v: Visit, p: NonNullable<Visit["execOwner"]>, text: string) => {
    const body: Parameters<typeof patchVisit>[1] = {
      ...(p.notifiedAt ? { notified: true } : {}),
      ...("waitAt" in p ? { wait: !!p.waitAt } : {}),
      ...(p.tech ? { owner_tech_id: Number(p.tech) } : {}),
      ...(p.doctor ? { owner_doctor_id: Number(p.doctor) } : {}),
    };
    return logTo(await run(patchVisit(v, body)), v.id, text);
  };
  const doneExec = async (v: Visit) => logTo(await run(execDoneVisit(v), `${v.cust.name} làm xong, đã trả phòng`), v.id, `Làm dịch vụ xong · trả ${v.exec?.room}`);
  /** Khách về, huỷ lượt: trả người và phòng đang giữ */
  const cancelVisit = async (v: Visit, reason: string) => {
    const r = await run(finishVisit(v, "cancelled", reason), `Đã huỷ lượt ${v.code}`);
    if (r) { logTo(true, v.id, `Huỷ lượt: ${reason}`); setSel(undefined); }
    return r;
  };

  /* ---------- Bảng ---------- */

  const queueCols: ColumnsType<Visit> = [
    { title: "Giờ", width: 82, render: (_, v) => <div><b>{hm(v.checkInAt)}</b><div className="text-[11px] text-slate-500">{v.status === "waiting" ? `chờ ${minutes(now - (v.checkInAt ?? now))}′` : v.startedAt ? `nhận sau ${minutes(v.startedAt - (v.checkInAt ?? v.startedAt))}′` : ""}</div></div> },
    { title: "Khách", width: 190, render: (_, v) => <div className="min-w-0"><b>{v.cust.name}</b><DeclBadge v={v} /><div className="text-[11.5px] text-slate-500">{v.cust.code} · {v.cust.phone}</div></div> },
    { title: "Đăng ký trước", width: 220, render: (_, v) => <div className="line-clamp-2 text-[12.5px]">{v.mang} · {v.apptSvcs?.map((s) => s.name).join(", ") || v.need}</div> },
    lane === "exec" ? {
      title: "Người phụ trách", width: 190,
      render: (_, v) => {
        const o = ownerOf(v);
        const b = ownerBusy(v);
        return (
          <div className="text-[12.5px]">
            <div>{o.tech ? techName(o.tech) : <span className="text-slate-400">Chưa có KTV</span>}</div>
            <div className="text-slate-500">{o.doctor ? doctorName(o.doctor) : "Không cần bác sĩ"}</div>
            {b && <div className="font-medium text-red-600">{v.execOwner?.waitAt ? "Khách đang chờ" : "Đang bận"}</div>}
          </div>
        );
      },
    } : { title: "Tư vấn viên · phòng", width: 170, render: (_, v) => (v.consultant ? <div className="text-[12.5px]"><b>{who(v.consultant)}</b><div className="text-slate-500">{v.room ?? "Chưa giữ phòng"}</div></div> : <span className="text-[12.5px] text-slate-400">Chưa phân</span>) },
    {
      title: "Tình trạng",
      width: 180,
      render: (_, v) => {
        const f = flowOf(v, now);
        const w = warns.get(v.id) ?? [];
        return (
          <div className="space-y-1">
            <Tag color={f.color} className="!m-0">{f.label}</Tag>
            {w.map((x) => <div key={x} className="flex items-center gap-1 text-[11.5px] font-medium text-red-600"><AlertTriangle size={12} />{WARN_TEXT[x]}</div>)}
          </div>
        );
      },
    },
  ];
  /** Lịch đặt trước chưa đến nằm chung bảng hàng chờ — cột giống khách đã check-in, trạng thái "Đặt trước" */
  const bookCell: Record<number, (b: Booking) => React.ReactNode> = {
    0: (b) => <div><b>{hm(b.at)}</b><div className="text-[11px] text-slate-500">giờ hẹn</div></div>,
    1: (b) => <div className="min-w-0"><b>{b.name}</b><div className="text-[11.5px] text-slate-500">{b.code} · {b.source}</div></div>,
    2: (b) => <div className="line-clamp-2 text-[12.5px]">{b.services}</div>,
    3: (b) => <div className="text-[12.5px] text-slate-500">{b.owner ?? "Chưa phân"}</div>,
    4: (b) => <Tag color={BOOK_ST[b.status]} className="!m-0">Đặt trước · {b.status}{b.status === "Trễ" ? ` ${dur(minutes(now - b.at))}` : ""}</Tag>,
  };
  const rowCols: ColumnsType<QRow> = queueCols.map((c, i) => ({ ...c, render: (_: unknown, r: QRow, k: number) => (r.v ? (c.render as any)(_, r.v, k) : bookCell[i](r.b!)) })); // eslint-disable-line @typescript-eslint/no-explicit-any

  return (
    <Layout>
      <Helmet><title>Lễ tân | Khothi</title></Helmet>
      <div className="mx-auto w-full max-w-[1800px] space-y-3 pb-24 text-slate-800 dark:text-slate-200">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-gray-900 dark:text-gray-50 sm:text-xl">Lễ tân</h1>
            <p className="text-[13px] text-slate-500 dark:text-slate-400">{viDay(now)} · check-in → phân tư vấn viên + phòng → xếp làm dịch vụ</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {branches.length > 1 && (
              <Select size="middle" value={branchId} onChange={(b) => { setSel(undefined); set({ branchId: b }); }} className="min-w-[170px]" aria-label="Cơ sở"
                options={branches.map((b) => ({ value: String(b.id), label: b.name }))} />
            )}
            <Tooltip title="Bật: hệ thống tự chia khách mới cho tư vấn viên rảnh; Lễ tân vẫn đổi được. Tắt: Lễ tân tự chọn.">
              <span className="flex items-center gap-1.5 text-[13px]"><Switch size="small" checked={autoAssign} onChange={(x) => set({ autoAssign: x })} />Chia tự động</span>
            </Tooltip>
            {waitingNew > 0 && (
              <Tooltip title="Chia ngay khách đang chờ cho tư vấn viên rảnh, ít lượt nhất, kèm giữ phòng tư vấn trống">
                <Button type="primary" icon={<Shuffle size={15} />} className="!bg-[#3e91ab]" loading={busy} onClick={autoAll}>Chia khách đang chờ ({waitingNew})</Button>
              </Tooltip>
            )}
            <Link to="/check-in" target="_blank"><Button icon={<QrCode size={15} />}>Kiosk check-in</Button></Link>
            <Link to="/app-tuvan"><Button icon={<ExternalLink size={15} />}>App Tư vấn</Button></Link>
          </div>
        </div>

        {syncError && <Alert type="warning" showIcon message={`Chưa tải được dữ liệu Lễ tân từ hệ thống: ${syncError}. Đang hiện dữ liệu lần tải gần nhất.`} />}

        <div className="grid grid-cols-1 gap-2 xl:grid-cols-2">
          <Summary
            title="Khách hôm nay"
            items={[
              { label: "Hàng chờ tư vấn", value: countOf("all", "consult"), onClick: () => setTab("consult"), hint: "Khách mới: đã check-in + đặt trước chưa đến" },
              { label: "Hàng chờ thực hiện", value: countOf("all", "exec"), onClick: () => setTab("exec"), hint: "Khách đã có lịch làm DV / liệu trình / đã mua" },
              { label: "Đặt trước chưa đến", value: notCome, onClick: () => setTab("consult", "booked") },
              { label: "Chờ phân tư vấn", value: waitingNew, warn: waitingNew > 0, onClick: () => showQueue("unassigned") },
              { label: "Cần xử lý", value: warnCount, warn: warnCount > 0, onClick: () => showQueue("warn"), hint: "Chờ lâu · tư vấn viên chưa nhận · đến giờ làm dịch vụ" },
            ]}
          />
          <Summary
            title="Đang trống"
            items={[
              { label: "Tư vấn viên", value: `${freeCons}/${cons.length}`, good: freeCons > 0, warn: freeCons === 0, onClick: () => showCal("cons") },
              { label: "Bác sĩ", value: `${freeDocs}/${docs.length}`, good: freeDocs > 0, warn: freeDocs === 0, onClick: () => showCal("doctor") },
              { label: "Kỹ thuật viên", value: `${freeTechs}/${techs.length}`, good: freeTechs > 0, warn: freeTechs === 0, onClick: () => showCal("tech") },
              { label: "Phòng tư vấn", value: `${freeConsultRooms}/${rooms.filter((r) => r.kind === "consult").length}`, good: freeConsultRooms > 0, warn: freeConsultRooms === 0, onClick: () => showCal("room") },
              { label: "Phòng thủ thuật", value: `${freeExecRooms}/${rooms.filter((r) => r.kind === "exec").length}`, good: freeExecRooms > 0, warn: freeExecRooms === 0, onClick: () => showCal("room") },
            ]}
          />
        </div>

        <div className="grid min-w-0 grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_400px] xl:grid-cols-[minmax(0,1fr)_440px]">
          <Box
            title={tab === "consult" ? "Hàng chờ tư vấn · khách mới" : tab === "exec" ? "Hàng chờ thực hiện · khách đã có lịch / liệu trình" : "Lịch theo giờ hôm nay"}
            icon={tab === "calendar" ? <CalendarRange size={17} className="text-[#3e91ab]" /> : tab === "exec" ? <CalendarClock size={17} className="text-[#3e91ab]" /> : <UserCheck size={17} className="text-[#3e91ab]" />}
            extra={(
              <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
                <Segmented
                  value={tab}
                  onChange={(x) => setTab(x as Tab)}
                  options={[
                    { value: "consult", label: `Hàng chờ tư vấn (${countOf("all", "consult")})` },
                    { value: "exec", label: `Hàng chờ thực hiện (${countOf("all", "exec")})` },
                    { value: "calendar", label: "Lịch theo giờ" },
                  ]}
                />
                {tab !== "calendar" && <Input allowClear value={q} onChange={(e) => setQ(e.target.value)} prefix={<Search size={14} className="text-slate-400" />} placeholder="Tên · mã · SĐT" className="min-w-[160px] flex-1 sm:w-48 sm:flex-none" />}
              </div>
            )}
          >
            {apiErr && <Alert type="warning" showIcon className="!mb-3" message="Chưa tải được lịch hẹn trên hệ thống — đang hiện lịch Telesales trên máy." />}
            {tab !== "calendar" && (
              <div className="mb-3 space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap gap-1.5">
                    {FILTERS[lane].map(([k, label]) => {
                      const n = countOf(k, lane);
                      const on = qf === k;
                      const hot = (k === "warn" || k === "unassigned" || k === "busy") && n > 0;
                      return (
                        <button
                          key={k}
                          type="button"
                          onClick={() => setQf(k)}
                          className={`rounded-full border px-3 py-1 text-[12.5px] font-medium transition-colors ${on
                            ? "border-[#3e91ab] bg-[#3e91ab] text-white"
                            : hot ? "border-orange-300 bg-orange-50 text-orange-700 hover:border-orange-500 dark:bg-orange-500/10 dark:text-orange-300"
                              : "border-slate-200 text-slate-600 hover:border-[#3e91ab] hover:text-[#3e91ab] dark:border-slate-700 dark:text-slate-300"}`}
                        >
                          {label} <span className={on ? "text-white/80" : "text-slate-400"}>{n}</span>
                        </button>
                      );
                    })}
                  </div>
                  <Segmented
                    size="small" value={view} onChange={(x) => setView(x as typeof view)}
                    options={[
                      { value: "list", label: "Danh sách" },
                      { value: "room", label: "Theo phòng" },
                      { value: "doctor", label: "Theo bác sĩ" },
                      { value: "staff", label: lane === "exec" ? "Theo KTV" : "Theo tư vấn viên" },
                    ]}
                  />
                </div>
                {qf === "booked" && (
                  <div className="flex flex-wrap items-center gap-2">
                    {[["Hôm nay", 0], ["Ngày mai", 1]].map(([label, n]) => {
                      const d = dayjs(now).startOf("day").add(n as number, "day");
                      const on = d.isSame(day, "day");
                      return <Button key={label} size="small" type={on ? "primary" : "default"} className={on ? "!bg-[#3e91ab]" : ""} onClick={() => setDay(d)}>{label}</Button>;
                    })}
                    <DatePicker size="small" value={day} onChange={(d) => d && setDay(d.startOf("day"))} format={(d) => viDay(d.valueOf())} allowClear={false} />
                    {!isTodayView && day.isAfter(now) && <span className="text-xs text-slate-500">Lịch sắp tới — gọi xác nhận trước ngày hẹn.</span>}
                  </div>
                )}
              </div>
            )}
            {tab === "calendar" && (
              <div className="space-y-3">
                <Segmented
                  value={tlKind}
                  onChange={(x) => setTlKind(x as TimelineKind)}
                  options={[
                    { value: "cons", label: `Tư vấn viên (${freeCons}/${cons.length} trống)` },
                    { value: "doctor", label: `Bác sĩ (${freeDocs}/${docs.length} trống)` },
                    { value: "tech", label: `Kỹ thuật viên (${freeTechs}/${techs.length} trống)` },
                    { value: "room", label: "Phòng" },
                  ]}
                  className="max-w-full overflow-x-auto"
                />
                <Timeline
                  rows={timeline(visits, branchId, now, tlKind, refs)}
                  booked={bookings.map((b): Marker => ({ at: b.at, label: `${b.name} · ${b.status}`, tone: b.status === "Đã check-in" ? "came" : b.status === "Trễ" || b.status === "Không đến" ? "late" : "book" }))}
                  checkins={today.filter((v) => v.checkInAt).map((v): Marker => ({ at: v.checkInAt!, label: `${v.cust.name} · ${flowOf(v, now).label}`, tone: "checkin" }))}
                  now={now}
                  onPick={(id) => setSel(id)}
                />
              </div>
            )}
            {tab !== "calendar" && view !== "list" && (
              <Columns
                rows={rows} lane={lane} view={view} sel={sel} now={now} rooms={rooms} cons={cons} techs={techs} docs={docs}
                ownerOf={ownerOf} ownerBusy={ownerBusy} onPick={setSel}
              />
            )}
            <div className={`w-full min-w-0 overflow-x-auto ${tab === "calendar" || view !== "list" ? "hidden" : ""}`}>
              <Table<QRow>
                rowKey="key" size="small" pagination={false} dataSource={rows} columns={rowCols} scroll={{ x: 820 }}
                onRow={(r) => ({ onClick: () => setSel(r.v?.id ?? r.b!.visit?.id ?? r.key), className: "cursor-pointer" })}
                rowClassName={(r) => (r.key === sel ? "[&>td]:!bg-[#e5f3f6] dark:[&>td]:!bg-[#1e3f49]"
                  : r.b ? "[&>td]:!bg-slate-50/80 dark:[&>td]:!bg-slate-800/40"
                    : (r.v && (warns.get(r.v.id)?.length || ownerBusy(r.v))) ? "[&>td]:!bg-red-50/70 dark:[&>td]:!bg-red-500/10" : "")}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={`Không có khách “${FILTERS[lane].find((f) => f[0] === qf)?.[1] ?? ""}”`} /> }}
              />
            </div>
          </Box>

          <aside className="h-fit min-w-0 space-y-3 lg:sticky lg:top-3">
            {visit ? (
              <VisitPanel
                v={visit} now={now} rooms={rooms} cons={cons} techs={techs} docs={docs}
                busy={busy} onAssign={assign} onExec={startExec} onDone={doneExec} onCancel={cancelVisit}
                owner={isExec(visit) ? ownerOf(visit) : undefined} ownerBusy={ownerBusy(visit)} onOwner={setOwner}
              />
            ) : booking ? (
              <Box title="Check-in khách" icon={<UserCheck size={17} className="text-[#3e91ab]" />}>
                <div className="mb-3 rounded-md bg-slate-50 p-3 dark:bg-slate-800">
                  <div className="text-lg font-bold text-slate-900 dark:text-slate-50">{booking.name}</div>
                  <div className="text-xs text-slate-500">{booking.code} · hẹn {hm(booking.at)} · {booking.source}{booking.owner ? ` · ${booking.owner}` : ""}</div>
                </div>
                <dl className="grid grid-cols-[110px_minmax(0,1fr)] gap-x-2 gap-y-1.5 text-sm">
                  <Row label="Dịch vụ đăng ký">{booking.services}</Row>
                  <Row label="Trạng thái"><Tag color={BOOK_ST[booking.status]} className="!m-0">{booking.status}</Tag></Row>
                </dl>
                {!isTodayView && <Alert className="!mt-3" type="info" showIcon message={`Lịch ngày ${day.format("DD/MM")} — check-in khi khách đến đúng ngày hẹn.`} />}
                {isTodayView && booking.status !== "Đã check-in" && booking.status !== "Không đến" && (
                  <Popconfirm title={`Check-in ${booking.name}?`} description={booking.exec ? "Khách vào Hàng chờ thực hiện." : "Khách vào Hàng chờ tư vấn, chờ phân tư vấn viên."} okText="Check-in" cancelText="Huỷ" onConfirm={() => checkIn(booking)}>
                    <Button type="primary" block size="large" loading={busy} className="!mt-4 !bg-[#3e91ab] font-bold" icon={<UserCheck size={16} />}>Xác nhận check-in</Button>
                  </Popconfirm>
                )}
                {booking.status === "Đã check-in" && <Alert className="!mt-3" type="success" showIcon message="Khách đã check-in. Lượt của khách đang được tải về hàng chờ." />}
              </Box>
            ) : (
              <Box title="Chọn khách"><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Bấm một khách để phân tư vấn / xếp phòng" /></Box>
            )}
          </aside>
        </div>

        <Board rooms={rooms} cons={cons} techs={techs} docs={docs} now={now} onPick={(v) => { setSel(v.id); setTab(isExec(v) ? "exec" : "consult"); }} />
      </div>
      <SoftBar />
    </Layout>
  );
}

/* ---------- Khách đang chọn: phân tư vấn viên + phòng, xếp làm dịch vụ ---------- */

function VisitPanel({ v, now, rooms, cons, techs, docs, busy, onAssign, onExec, onDone, onCancel, owner, ownerBusy, onOwner }: {
  v: Visit;
  busy: boolean;
  onCancel: (v: Visit, reason: string) => Promise<boolean>;
  now: number;
  rooms: ReturnType<typeof roomBoard>;
  cons: ReturnType<typeof consultantBoard>;
  techs: ReturnType<typeof techBoard>;
  docs: ReturnType<typeof doctorBoard>;
  onAssign: (v: Visit, consultant: string, room: string) => Promise<boolean>;
  onExec: (v: Visit, room: string, tech: string, doctor?: string) => Promise<boolean>;
  onDone: (v: Visit) => Promise<boolean>;
  /** Khách Hàng chờ thực hiện: KTV / bác sĩ phụ trách */
  owner?: { tech?: string; doctor?: string };
  /** Người phụ trách đang bận (mô tả) */
  ownerBusy?: string;
  onOwner: (v: Visit, p: NonNullable<Visit["execOwner"]>, text: string) => Promise<boolean>;
}) {
  const consRooms = freeRooms(rooms, "consult", v);
  const execRooms = freeRooms(rooms, "exec", v);
  const sugCons = suggestConsultant(cons.filter((c) => c.id !== v.consultant));
  const sugTech = suggestTech(techs, v.mang);
  // Buổi App Tư vấn đã hẹn hôm nay: lấy phòng / bác sĩ theo lịch nếu đang trống
  const sess = todaySessions(v, now)[0]?.s;
  const docFree = (id?: string) => !!id && !docs.find((d) => d.id === id)?.visit;
  const pickExecRoom = () => (sess?.room && execRooms.includes(sess.room) ? sess.room : execRooms[0]);
  const pickDoctor = () => (docFree(sess?.doctor) ? sess?.doctor : undefined);
  const [cid, setCid] = useState<string | undefined>(v.consultant ?? sugCons?.id);
  const [room, setRoom] = useState<string | undefined>(v.room ?? consRooms[0]);
  const [eRoom, setERoom] = useState<string | undefined>(pickExecRoom);
  const techFree = (id?: string) => !!id && !techs.find((t) => t.tech.id === id)?.visit;
  const pickTech = () => (techFree(owner?.tech) ? owner?.tech : sugTech?.tech.id);
  const [tech, setTech] = useState<string | undefined>(pickTech);
  const [doc, setDoc] = useState<string | undefined>(() => (owner?.doctor && docFree(owner.doctor) ? owner.doctor : pickDoctor()));
  const [execOpen, setExecOpen] = useState(false);
  const [reason, setReason] = useState("");
  // Đổi khách → điền lại gợi ý
  useEffect(() => {
    setExecOpen(false);
    setCid(v.consultant ?? sugCons?.id);
    setRoom(v.room ?? consRooms[0]);
    setERoom(pickExecRoom());
    setTech(pickTech());
    setDoc(owner?.doctor && docFree(owner.doctor) ? owner.doctor : pickDoctor());
  }, [v.id, cons.length > 0]); // eslint-disable-line react-hooks/exhaustive-deps
  // Máy khác vừa giữ phòng đang chọn thì đổi sang phòng còn trống, không để Lễ tân bấm lại vào phòng đã bận
  useEffect(() => { if (room && !consRooms.includes(room)) setRoom(consRooms[0]); }, [consRooms.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (eRoom && !execRooms.includes(eRoom)) setERoom(execRooms[0]); }, [execRooms.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps

  const f = flowOf(v, now);
  const w = warnsOf(v, now);
  /** Tự chia cho khách này: người gợi ý + phòng trống, phân luôn */
  const autoOne = () => {
    if (!sugCons) { message.info("Chưa có tư vấn viên trống"); return; }
    const r = v.room ?? consRooms[0];
    onAssign(v, sugCons.id, r ?? "");
  };
  const started = v.status !== "waiting";
  const changed = cid !== v.consultant || (room ?? "") !== (v.room ?? "");
  const execOn = v.exec && !v.exec.doneAt;
  const dep = (v.onlineDeps ?? []).filter((d) => d.received).reduce((s, d) => s + d.amount, 0);

  const showExec = execOpen || !!v.exec || !!sess || !!owner;
  const ownerNames = [owner?.tech && techName(owner.tech), owner?.doctor && doctorName(owner.doctor)].filter(Boolean).join(", ");
  // Khách Hàng chờ thực hiện: bước Làm dịch vụ lên trước, phân tư vấn viên chỉ khi khách muốn tư vấn thêm
  const nConsult = owner ? 2 : 1;
  const nExec = owner ? 1 : 2;

  return (
    <section className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
      {/* Đầu thẻ: khách + tình trạng */}
      <div className="border-b border-slate-100 bg-gradient-to-r from-[#e8f4f7] to-white px-4 py-3 dark:border-slate-800 dark:from-[#3e91ab]/15 dark:to-transparent">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500"><Users size={13} />Khách đang chọn</div>
            <div className="truncate text-lg font-bold text-slate-900 dark:text-slate-50">{v.cust.name}<DeclBadge v={v} /></div>
            <div className="text-xs text-slate-500">{v.cust.code} · {v.cust.phone} · check-in {hm(v.checkInAt)}{v.status === "waiting" ? ` · chờ ${minutes(now - (v.checkInAt ?? now))} phút` : ""}</div>
          </div>
          <Tag color={f.color} className="!m-0 shrink-0">{f.label}</Tag>
        </div>
        {w.length > 0 && (
          <div className="mt-2 space-y-0.5">
            {w.map((x) => <div key={x} className="flex items-center gap-1 text-[12.5px] font-semibold text-red-600"><AlertTriangle size={13} />{WARN_TEXT[x]}</div>)}
          </div>
        )}
      </div>
      <div className="px-4 py-3">
        <dl className="grid grid-cols-[100px_minmax(0,1fr)] gap-x-2 gap-y-1 text-[13px]">
          <Row label="Mảng">{v.mang}</Row>
          <Row label="Đăng ký trước">
            {v.apptSvcs?.length
              ? <ul className="list-disc pl-4">{v.apptSvcs.map((s) => <li key={s.id}>{s.name}{s.qty > 1 ? ` ×${s.qty}` : ""}</li>)}</ul>
              : v.need}
          </Row>
          {v.online && <Row label="Báo giá online">{v.online.code}{v.online.total ? ` · từ ${vnd(v.online.total)}` : ""}{v.online.promo ? ` · ${v.online.promo}` : ""}</Row>}
          {dep > 0 && <Row label="Cọc đã nhận">{vnd(dep)}</Row>}
          <Row label="Nguồn">{v.source}{v.owner ? ` · ${v.owner}` : ""}</Row>
          {v.flags?.length ? <Row label="Lưu ý">{v.flags.map((x) => <Tag key={x} color="red" className="!mb-1">{x}</Tag>)}</Row> : null}
        </dl>
      </div>

      <div className={owner ? "order-2 flex flex-col" : ""}>
      <Step n={nConsult} title={owner ? "Phân tư vấn viên (nếu khách muốn tư vấn thêm)" : "Phân tư vấn viên"} done={!!v.consultant} optional={!!owner}>
        {execOn ? (
          <div className="text-sm text-slate-500">Khách đang trong phòng thủ thuật. Phân tư vấn viên sau khi trả phòng.</div>
        ) : started && !v.consultant ? (
          <div className="text-sm text-slate-500">Khách làm dịch vụ, không qua tư vấn.</div>
        ) : started ? (
          <div className="text-sm">
            <b>{who(v.consultant)}</b> {v.status === "consulting" ? "đang tư vấn" : "đã tư vấn xong"}{v.room ? ` · ${v.room}` : ""}.
            <Link to={`/phieu-tu-van?v=${v.id}`} className="ml-1 text-[#3e91ab] hover:underline">Xem phiếu</Link>
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <label className="block">
              <span className="text-slate-500">Tư vấn viên</span>
              <Select
                showSearch
                optionFilterProp="title"
                optionLabelProp="title"
                value={cid}
                onChange={setCid}
                className="mt-1 w-full"
                placeholder="Chọn tư vấn viên"
                options={cons.map((c) => ({
                  value: c.id,
                  title: c.name,
                  disabled: c.st === "break" || c.st === "off",
                  label: (
                    <span className="flex items-center justify-between gap-2">
                      <span className="truncate">{c.name}{c.id === sugCons?.id ? " · gợi ý" : ""}</span>
                      <span className="shrink-0 text-xs text-slate-500">
                        {CONS_ST[c.st].label}{c.held.length ? ` · ${c.held.length} chờ` : ""} · {c.served} lượt
                      </span>
                    </span>
                  ),
                }))}
              />
            </label>
            <label className="block">
              <span className="text-slate-500">Phòng tư vấn</span>
              <Select
                optionLabelProp="title"
                value={room}
                onChange={setRoom}
                className="mt-1 w-full"
                placeholder={consRooms.length ? "Chọn phòng" : "Hết phòng trống"}
                options={rooms.filter((r) => r.kind === "consult").map((r) => ({
                  value: r.name,
                  title: r.name,
                  disabled: !consRooms.includes(r.name),
                  label: <span className="flex justify-between gap-2"><span>{r.name}</span><span className="text-xs text-slate-500">{consRooms.includes(r.name) ? "Trống" : `${r.visit?.cust.name} · ${hm(r.since)}`}</span></span>,
                }))}
              />
            </label>
            {!consRooms.length && <Alert type="warning" showIcon message="Hết phòng tư vấn trống — phân tư vấn viên trước, phòng giữ sau khi có phòng trống." />}
            <div className="flex gap-2">
              <Button
                type="primary" size="large" disabled={!cid || !changed} loading={busy}
                className="flex-1 !bg-[#3e91ab] font-bold disabled:!bg-slate-200"
                onClick={() => { if (cid) onAssign(v, cid, room ?? ""); }}
              >
                {v.consultant ? "Đổi tư vấn viên / phòng" : "Chuyển cho tư vấn viên"}
              </Button>
              {!v.consultant && (
                <Tooltip title="Hệ thống chọn tư vấn viên rảnh, ít lượt nhất và phòng trống">
                  <Button size="large" icon={<Shuffle size={15} />} onClick={autoOne}>Tự chia</Button>
                </Tooltip>
              )}
            </div>
            {v.consultant && <div className="text-xs text-slate-500">Đang chờ {who(v.consultant)} nhận khách ở App Tư vấn · phân lúc {hm(v.assignedAt)}</div>}
          </div>
        )}
      </Step>
      </div>

      <div className={owner ? "order-1" : ""}>
      <Step n={nExec} title="Làm dịch vụ" done={!!v.exec?.doneAt} optional={!showExec}>
        {!showExec ? (
          <button
            type="button"
            onClick={() => setExecOpen(true)}
            className="w-full rounded-md border border-dashed border-slate-300 px-3 py-2 text-left text-[13px] text-slate-500 hover:border-[#3e91ab] hover:text-[#3e91ab] dark:border-slate-600"
          >
            + Xếp làm dịch vụ hôm nay (phòng thủ thuật · kỹ thuật viên · bác sĩ)
          </button>
        ) : execOn ? (
          <div className="space-y-3 text-sm">
            <div><b>{v.exec!.room}</b> · {techName(v.exec!.tech)}{v.exec!.doctor ? ` · ${doctorName(v.exec!.doctor)}` : ""} · vào lúc {hm(v.exec!.at)} ({minutes(now - v.exec!.at)} phút)</div>
            <Popconfirm title="Khách làm xong, trả phòng?" okText="Trả phòng" cancelText="Huỷ" onConfirm={() => onDone(v)}>
              <Button block loading={busy}>Xong · trả phòng</Button>
            </Popconfirm>
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            {v.exec?.doneAt && (
              <div className="space-y-2">
                <div className="text-xs text-slate-500">Đã làm ở {v.exec.room} · {techName(v.exec.tech)} · xong {hm(v.exec.doneAt)}</div>
                {/* Khách dùng thẻ liệu trình: trừ buổi ở App Nội khoa, tìm sẵn theo SĐT */}
                <Link to="/treatment/checkin" state={{ q: v.phoneDigits ?? "" }}><Button block icon={<ExternalLink size={14} />}>Trừ buổi ở App Nội khoa</Button></Link>
              </div>
            )}
            {v.status !== "done" && (<>
            {owner && !v.exec && (
              <div className={`rounded-md px-3 py-2 text-[12.5px] ${ownerBusy ? "bg-red-50 text-red-800 dark:bg-red-500/10 dark:text-red-200" : "bg-emerald-50 text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-200"}`}>
                <div>Phụ trách: <b>{ownerNames || "chưa có — chọn KTV bên dưới"}</b></div>
                {ownerBusy ? <div className="mt-0.5 flex items-center gap-1 font-semibold"><AlertTriangle size={13} />{ownerBusy}</div> : ownerNames && <div className="mt-0.5">Đang trống — đưa khách vào phòng được ngay</div>}
                {v.execOwner?.notifiedAt && <div className="mt-0.5 text-xs opacity-80">Đã báo người phụ trách lúc {hm(v.execOwner.notifiedAt)}</div>}
                {v.execOwner?.waitAt && <div className="mt-0.5 text-xs opacity-80">Khách chấp nhận chờ từ {hm(v.execOwner.waitAt)} ({minutes(now - v.execOwner.waitAt)} phút)</div>}
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {ownerNames && !v.execOwner?.notifiedAt && <Button size="small" onClick={() => { onOwner(v, { notifiedAt: Date.now() }, `Báo ${ownerNames}: khách ${v.cust.name} đã đến`).then((r) => { if (r) message.success(`Đã ghi nhận báo ${ownerNames}`); }); }}>Báo người phụ trách</Button>}
                  {ownerBusy && !v.execOwner?.waitAt && <Button size="small" onClick={() => onOwner(v, { waitAt: Date.now() }, `Khách chấp nhận chờ ${ownerNames}`)}>Khách chờ</Button>}
                  {ownerBusy && (
                    <Button size="small" danger onClick={() => {
                      const t = sugTech?.tech.id;
                      const d = owner.doctor ? docs.find((x) => !x.visit)?.id : undefined;
                      onOwner(v, { tech: t, doctor: d, waitAt: undefined }, `Đổi người phụ trách → ${[t && techName(t), d && doctorName(d)].filter(Boolean).join(", ") || "chưa có người trống"}`);
                      setTech(t); setDoc(d);
                    }}>Đổi người khác</Button>
                  )}
                </div>
              </div>
            )}
            {sess && !v.exec && (
              <div className="rounded-md bg-fuchsia-50 px-3 py-2 text-[12.5px] text-fuchsia-800 dark:bg-fuchsia-500/10 dark:text-fuchsia-200">
                Tư vấn đã hẹn làm dịch vụ lúc <b>{hm(sess.at)}</b>{sess.room ? ` · ${sess.room}` : ""}{sess.doctor ? ` · ${doctorName(sess.doctor)}` : ""}
                {sess.room && !execRooms.includes(sess.room) && " — phòng này đang bận, chọn phòng khác"}
                {sess.doctor && !docFree(sess.doctor) && " — bác sĩ đang bận"}
              </div>
            )}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
              <label className="block">
                <span className="text-slate-500">Phòng thủ thuật</span>
                <Select
                  optionLabelProp="title" value={eRoom} onChange={setERoom} className="mt-1 w-full" placeholder={execRooms.length ? "Chọn phòng" : "Hết phòng trống"}
                  options={rooms.filter((r) => r.kind === "exec").map((r) => ({
                    value: r.name, title: r.name, disabled: !execRooms.includes(r.name),
                    label: <span className="flex justify-between gap-2"><span>{r.name}</span><span className="text-xs text-slate-500">{execRooms.includes(r.name) ? "Trống" : "Đang dùng"}</span></span>,
                  }))}
                />
              </label>
              <label className="block">
                <span className="text-slate-500">Kỹ thuật viên</span>
                <Select
                  showSearch optionFilterProp="title" optionLabelProp="title" value={tech} onChange={setTech} className="mt-1 w-full" placeholder="Chọn KTV"
                  options={techs.map((t) => ({
                    value: t.tech.id, title: t.tech.name, disabled: !!t.visit,
                    label: (
                      <span className="flex justify-between gap-2">
                        <span className="truncate">{t.tech.name}</span>
                        <span className="shrink-0 text-xs text-slate-500">{t.visit ? "Đang làm" : t.tech.mangs.includes(v.mang) ? `Trống · ${v.mang}` : "Trống"}</span>
                      </span>
                    ),
                  }))}
                />
              </label>
              <label className="block sm:col-span-2 lg:col-span-1 xl:col-span-2">
                <span className="text-slate-500">Bác sĩ (nếu dịch vụ cần)</span>
                <Select
                  allowClear showSearch optionFilterProp="title" optionLabelProp="title" value={doc} onChange={setDoc} className="mt-1 w-full" placeholder="Không cần bác sĩ"
                  options={docs.map((d) => ({
                    value: d.id, title: d.name, disabled: !!d.visit,
                    label: (
                      <span className="flex justify-between gap-2">
                        <span className="truncate">{d.name}</span>
                        <span className="shrink-0 text-xs text-slate-500">{d.visit ? `${d.why === "exec" ? "Đang làm DV" : "Đang khám"} · ${d.visit.cust.name}` : "Trống"}</span>
                      </span>
                    ),
                  }))}
                />
              </label>
            </div>
            <Button block type="primary" ghost disabled={!eRoom || !tech} loading={busy} onClick={() => { onExec(v, eRoom!, tech!, doc); }}>
              Đưa khách vào phòng
            </Button>
            </>)}
          </div>
        )}
      </Step>
      </div>

      {(v.status === "waiting" || v.status === "consulting") && !execOn && (
        <div className="order-3 border-t border-slate-100 px-4 py-2 dark:border-slate-800">
          <Popconfirm
            title="Huỷ lượt của khách?"
            description={<Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Lý do, VD: khách về, không chờ được" className="!mt-1 !w-[240px]" />}
            okText="Huỷ lượt" cancelText="Đóng" okButtonProps={{ danger: true, disabled: !reason.trim() }}
            onConfirm={async () => { if (await onCancel(v, reason.trim())) setReason(""); }}
          >
            <Button block type="text" size="small" danger>Khách về / huỷ lượt</Button>
          </Popconfirm>
        </div>
      )}
    </section>
  );
}

/** Một bước trong thẻ khách: số thứ tự + tiêu đề, xong thì có dấu tích */
const Step = ({ n, title, done, optional, children }: { n: number; title: string; done?: boolean; optional?: boolean; children: React.ReactNode }) => (
  <div className="border-t border-slate-100 px-4 py-3 dark:border-slate-800">
    <div className="mb-2 flex items-center gap-2 text-[13.5px] font-semibold text-slate-800 dark:text-slate-100">
      <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-bold ${done ? "bg-emerald-500 text-white" : optional ? "bg-slate-200 text-slate-500 dark:bg-slate-700" : "bg-[#3e91ab] text-white"}`}>{done ? "✓" : n}</span>
      {title}
    </div>
    {children}
  </div>
);

/* ---------- Bảng phòng + nhân sự ---------- */

function Board({ rooms, cons, techs, docs, now, onPick }: {
  rooms: ReturnType<typeof roomBoard>;
  cons: ReturnType<typeof consultantBoard>;
  techs: ReturnType<typeof techBoard>;
  docs: ReturnType<typeof doctorBoard>;
  now: number;
  onPick: (v: Visit) => void;
}) {
  const card = (busy: boolean) => `rounded-md border p-2.5 text-left text-[12.5px] transition-colors ${busy
    ? "border-orange-200 bg-orange-50/70 hover:border-orange-400 dark:border-orange-500/30 dark:bg-orange-500/10"
    : "border-emerald-200 bg-emerald-50/60 dark:border-emerald-500/30 dark:bg-emerald-500/10"}`;
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 2xl:grid-cols-4">
      <Box title="Phòng" icon={<DoorOpen size={17} className="text-[#3e91ab]" />}>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-2">
          {rooms.map((r) => (
            <button key={r.name} type="button" disabled={!r.visit} onClick={() => r.visit && onPick(r.visit)} className={card(!!r.visit)}>
              <div className="font-semibold text-slate-800 dark:text-slate-100">{r.name}</div>
              <div className="truncate text-slate-500">
                {r.visit
                  ? `${r.visit.cust.name} · ${r.kind === "consult" && r.visit.status === "waiting" ? "giữ chỗ" : dur(minutes(now - (r.since ?? now)))}`
                  : "Trống"}
              </div>
            </button>
          ))}
        </div>
      </Box>
      <Box title="Tư vấn viên" icon={<UserCheck size={17} className="text-[#3e91ab]" />}>
        <ul className="max-h-[320px] divide-y divide-slate-100 overflow-y-auto dark:divide-slate-800">
          {cons.map((c) => (
            <li key={c.id} className="flex items-center justify-between gap-2 py-1.5 text-[13px]">
              <span className="flex min-w-0 items-center gap-2">
                <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: c.st === "break" || c.st === "off" ? STAFF_ST[c.st].dot : c.color }} />
                <span className="truncate">{c.name}</span>
              </span>
              <span className="flex shrink-0 items-center gap-1.5">
                {c.visit && <button type="button" onClick={() => onPick(c.visit!)} className="max-w-[120px] truncate text-xs text-[#3e91ab] hover:underline">{c.visit.cust.name}</button>}
                <Tag color={CONS_ST[c.st].color} className="!m-0">{CONS_ST[c.st].label}{c.held.length ? ` · ${c.held.length}` : ""}</Tag>
              </span>
            </li>
          ))}
        </ul>
      </Box>
      <Box title="Bác sĩ" icon={<Stethoscope size={17} className="text-[#3e91ab]" />}>
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {docs.map((d) => (
            <li key={d.id} className="flex items-center justify-between gap-2 py-1.5 text-[13px]">
              <span className="min-w-0">
                <span className="block truncate">{d.name}</span>
                <span className="text-[11px] text-slate-500">{d.served} lượt hôm nay</span>
              </span>
              {d.visit
                ? (
                  <button type="button" onClick={() => onPick(d.visit!)}>
                    <Tag color={d.why === "exec" ? "purple" : "magenta"} className="!m-0">{d.why === "exec" ? "Làm DV" : "Đang khám"} · {d.visit.cust.name} · {dur(minutes(now - (d.since ?? now)))}</Tag>
                  </button>
                )
                : <Tag color="green" className="!m-0">Trống</Tag>}
            </li>
          ))}
        </ul>
      </Box>
      <Box title="Kỹ thuật viên" icon={<Stethoscope size={17} className="text-[#3e91ab]" />}>
        <ul className="max-h-[320px] divide-y divide-slate-100 overflow-y-auto dark:divide-slate-800">
          {techs.map((t) => (
            <li key={t.tech.id} className="flex items-center justify-between gap-2 py-1.5 text-[13px]">
              <span className="min-w-0">
                <span className="block truncate">{t.tech.name}</span>
                <span className="text-[11px] text-slate-500">{t.tech.mangs.length ? `${t.tech.mangs.join(" · ")} · ` : ""}{t.served} lượt</span>
              </span>
              {t.visit
                ? <button type="button" onClick={() => onPick(t.visit!)}><Tag color="orange" className="!m-0">{t.visit.exec?.room} · {t.visit.cust.name}</Tag></button>
                : <Tag color="green" className="!m-0">Trống</Tag>}
            </li>
          ))}
        </ul>
      </Box>
    </div>
  );
}

/* ---------- Hàng chờ dạng cột: theo phòng / bác sĩ / tư vấn viên hoặc KTV ---------- */

function Columns({ rows, lane, view, sel, now, rooms, cons, techs, docs, ownerOf, ownerBusy, onPick }: {
  rows: QRow[];
  lane: "consult" | "exec";
  view: "room" | "doctor" | "staff";
  sel?: string;
  now: number;
  rooms: ReturnType<typeof roomBoard>;
  cons: ReturnType<typeof consultantBoard>;
  techs: ReturnType<typeof techBoard>;
  docs: ReturnType<typeof doctorBoard>;
  ownerOf: (v: Visit) => { tech?: string; doctor?: string };
  ownerBusy: (v: Visit) => string | undefined;
  onPick: (id: string) => void;
}) {
  // Cột = phòng / bác sĩ / người phụ trách; trạng thái trống / bận của chính người hoặc phòng đó hiện ở đầu cột
  const cols: { id: string; name: string; busy?: string }[] = view === "room"
    ? rooms.filter((r) => r.kind === (lane === "exec" ? "exec" : "consult")).map((r) => ({ id: r.name, name: r.name, busy: r.visit ? r.visit.cust.name : undefined }))
    : view === "doctor"
      ? docs.map((d) => ({ id: d.id, name: d.name, busy: d.visit ? `${d.why === "exec" ? "Làm DV" : "Khám"} · ${d.visit.cust.name}` : undefined }))
      : lane === "exec"
        ? techs.map((t) => ({ id: t.tech.id, name: t.tech.name, busy: t.visit ? t.visit.cust.name : undefined }))
        : cons.map((c) => ({ id: c.id, name: c.name, busy: c.st === "busy" ? c.visit?.cust.name : c.st === "break" || c.st === "off" ? CONS_ST[c.st].label : undefined }));
  const colOf = (v: Visit): string | undefined => {
    if (view === "room") return lane === "exec" ? v.exec?.room ?? todaySessions(v, now)[0]?.s.room : v.room;
    if (view === "doctor") return lane === "exec" ? ownerOf(v).doctor : v.quote?.exam.doctor ?? v.invite?.doctor;
    return lane === "exec" ? ownerOf(v).tech : v.consultant;
  };
  const by = new Map<string, QRow[]>();
  rows.forEach((r) => {
    const k = (r.v && colOf(r.v)) || "_";
    by.set(k, [...(by.get(k) ?? []), r]);
  });
  const all = [...cols, { id: "_", name: view === "room" ? "Chưa xếp phòng" : view === "doctor" ? "Chưa có bác sĩ" : "Chưa phân", busy: undefined }];
  return (
    <div className="flex gap-2 overflow-x-auto pb-2">
      {all.map((c) => {
        const items = by.get(c.id) ?? [];
        if (c.id === "_" && !items.length) return null;
        return (
          <div key={c.id} className="w-[210px] shrink-0 rounded-lg border border-slate-200 bg-slate-50/70 dark:border-slate-700 dark:bg-slate-800/40">
            <div className="border-b border-slate-200 px-2.5 py-2 dark:border-slate-700">
              <div className="truncate text-[13px] font-semibold text-slate-800 dark:text-slate-100">{c.name}</div>
              <div className={`truncate text-[11.5px] ${c.id === "_" ? "text-slate-500" : c.busy ? "text-orange-600" : "text-emerald-600"}`}>
                {c.id === "_" ? `${items.length} khách` : c.busy ? `Đang bận · ${c.busy}` : "Đang trống"}{c.id !== "_" && items.length ? ` · ${items.length} khách chờ` : ""}
              </div>
            </div>
            <div className="space-y-1.5 p-1.5">
              {items.map((r) => {
                const v = r.v;
                const hot = v && (ownerBusy(v) || warnsOf(v, now).length);
                return (
                  <button
                    key={r.key} type="button" onClick={() => onPick(v?.id ?? r.b!.visit?.id ?? r.key)}
                    className={`w-full rounded-md border bg-white px-2 py-1.5 text-left text-[12px] transition-colors hover:border-[#3e91ab] dark:bg-slate-900 ${r.key === sel ? "border-[#3e91ab] ring-1 ring-[#3e91ab]" : hot ? "border-red-300" : "border-slate-200 dark:border-slate-700"}`}
                  >
                    <div className="truncate font-semibold text-slate-800 dark:text-slate-100">{v ? v.cust.name : r.b!.name}</div>
                    <div className="truncate text-slate-500">
                      {v ? `${hm(v.checkInAt)} · ${flowOf(v, now).label}` : `Đặt trước ${hm(r.b!.at)} · ${r.b!.status}`}
                    </div>
                    {v && ownerBusy(v) && <div className="truncate font-medium text-red-600">{v.execOwner?.waitAt ? "Khách đang chờ" : "Người phụ trách bận"}</div>}
                  </button>
                );
              })}
              {!items.length && <div className="px-1 py-2 text-center text-[11.5px] text-slate-400">Không có khách</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
